import * as React from "react"
import { Props as focusTrapProps } from "focus-trap-react"

import { Modal } from "@tenforce/toolbox-modal-react"
import { FieldItems, TextArea, ReadOnly } from "@tenforce/toolbox-field-react"
import {
    Action,
    ActionAnchor,
    ActionType,
} from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import Thumbnail from "../../common/Thumbnail"
import bem from "../../../bem"
import { Labels } from "../../../util/Translations"
import { IAttachment, ILightbox } from "../index"

export const className = "toolbox-lightbox"
const b = bem.with(className)

interface OwnProps {
    attachments: IAttachment[]
    canEditAttachments: boolean
    canDownloadAttachments: boolean
    customFieldDefinitionId: string

    hideLightbox: () => void
    lightbox: ILightbox

    onAttachmentLoad?: () => void
    onDeleteAttachment: (key: number) => void
    onUpdateAttachmentDescription: (key: number, value: string) => void
    changeAttachmentInLightbox: (diff: number) => void
}

type Props = OwnProps

interface State {
    unsyncDescription: string
    previousDescription: string
}

export default class Lightbox extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            unsyncDescription: "",
            previousDescription: "",
        }
    }
    containerRef = React.createRef<HTMLDivElement>()
    previousRef = React.createRef<HTMLButtonElement>()
    nextRef = React.createRef<HTMLButtonElement>()

    componentDidMount(): void {
        const {
            attachments,
            lightbox: { currentIndex },
        } = this.props

        if (
            attachments.length === 0 ||
            currentIndex < 0 ||
            currentIndex >= attachments.length
        ) {
            return
        }

        const currentAttachment = attachments[currentIndex]
        const description = currentAttachment.description ?? ""

        this.setState({
            unsyncDescription: description,
            previousDescription: description,
        })
    }

    componentDidUpdate(): void {
        const {
            attachments,
            lightbox: { currentIndex },
        } = this.props

        if (
            attachments.length === 0 ||
            currentIndex < 0 ||
            currentIndex >= attachments.length
        ) {
            return
        }

        const { previousDescription } = this.state
        const currentAttachment = attachments[currentIndex]
        const description = currentAttachment.description ?? ""

        if (description !== previousDescription) {
            this.setState({
                unsyncDescription: description,
                previousDescription: description,
            })
        }
    }

    render(): JSX.Element {
        const {
            attachments,
            canEditAttachments,
            canDownloadAttachments,
            lightbox: { currentIndex, isShown },
            changeAttachmentInLightbox,
            onAttachmentLoad,
            hideLightbox,
        } = this.props
        const { unsyncDescription } = this.state

        const { DeleteThisAttachment, AddDescriptionHere } = Labels

        if (
            !isShown ||
            attachments.length === 0 ||
            currentIndex < 0 ||
            currentIndex >= attachments.length
        ) {
            return <></>
        }

        const currentAttachment = attachments[currentIndex]

        const metaData = (
            <>
                <span data-ft-creator>
                    {currentAttachment.creator}&nbsp;{Symbols.Dot}&nbsp;
                </span>
                <span data-ft-file-properties>
                    {currentAttachment.fileProperties}
                </span>
            </>
        )

        const focusTrap: Partial<focusTrapProps> = {
            focusTrapOptions: {
                fallbackFocus: this.containerRef.current ?? "#lightboxModal",
            },
        }

        return (
            <Modal
                isShown={isShown}
                containerProps={{
                    id: "lightboxModal",
                    onClose: hideLightbox,
                    isCenterFixedPositioned: true,
                }}
                contentProps={{
                    extraClasses: b("modal"),
                }}
                focusTrapProps={focusTrap}
            >
                <div
                    ref={this.containerRef}
                    tabIndex={-1}
                    className={`${b()} is-inverted`}
                    onKeyDown={(event): boolean => {
                        const {
                            attachments,
                            lightbox: { currentIndex },
                        } = this.props

                        if (
                            event.key === "ArrowRight" &&
                            currentIndex < attachments.length - 1
                        ) {
                            this.props.changeAttachmentInLightbox(1)
                            this.setFocus()
                            return false
                        } else if (
                            event.key === "ArrowLeft" &&
                            currentIndex > 0
                        ) {
                            this.props.changeAttachmentInLightbox(-1)
                            this.setFocus()
                            return false
                        }
                        return true
                    }}
                >
                    <div className={b("top")} onClick={hideLightbox}></div>

                    <div className={b("inner")}>
                        <div
                            className={b("innerContainer")}
                            onClick={hideLightbox}
                        >
                            <Action
                                autoFocus={currentIndex > 0}
                                ref={this.previousRef}
                                extraClasses={`${b("nav")} ${
                                    currentIndex <= 0 && "is-invisible"
                                }`}
                                onClick={(
                                    event: React.MouseEvent<HTMLButtonElement>,
                                ): void => {
                                    event.stopPropagation()
                                    changeAttachmentInLightbox(-1)
                                    this.setFocus()
                                }}
                                data-ft-arrow="previous"
                            >
                                {Symbols.ArrowLeft}
                            </Action>
                        </div>

                        <div
                            className={`${b("image")} bg-darkcloud`}
                            data-ft-current-attachment
                        >
                            <header className="step-header">
                                <h4 data-ft-name>
                                    {currentAttachment.fileName}
                                </h4>
                                {canEditAttachments && (
                                    <Action
                                        actionType={ActionType.icon}
                                        title={DeleteThisAttachment}
                                        onClick={this.onAttachmentDelete}
                                        data-ft-delete
                                    >
                                        {Symbols.Delete}
                                    </Action>
                                )}
                            </header>

                            {currentAttachment.thumbnail ? (
                                <Thumbnail
                                    lowResSrc={currentAttachment.thumbnail}
                                    hiResSrc={currentAttachment.url}
                                    alt={currentAttachment.fileName}
                                    onLoad={onAttachmentLoad}
                                />
                            ) : (
                                <div className={b("fileContainer")}>
                                    {canDownloadAttachments ? (
                                        <ActionAnchor
                                            extraClasses={`${b(
                                                "fileDownload",
                                            )} theme-bubble bg-darkest`}
                                            target="_blank"
                                            rel="noreferrer"
                                            href={currentAttachment.url}
                                        >
                                            <h3>{Symbols.Item}</h3>
                                        </ActionAnchor>
                                    ) : (
                                        <div
                                            className={`${b(
                                                "fileReadonly",
                                            )} theme-bubble bg-darkest`}
                                        >
                                            <h3>{Symbols.Item}</h3>
                                        </div>
                                    )}
                                </div>
                            )}

                            <footer>
                                <div>
                                    <FieldItems
                                        isInverted={true}
                                        label={metaData}
                                    >
                                        {canEditAttachments ? (
                                            <TextArea
                                                data-ft-description
                                                autoSize={true}
                                                placeholder={AddDescriptionHere}
                                                value={unsyncDescription}
                                                onKeyDown={(
                                                    event: React.KeyboardEvent<HTMLTextAreaElement>,
                                                ): boolean => {
                                                    // we need to make sure we don't change attachments
                                                    if (
                                                        event.key ===
                                                            "ArrowRight" ||
                                                        event.key ===
                                                            "ArrowLeft"
                                                    ) {
                                                        event.stopPropagation()
                                                        event.nativeEvent.stopImmediatePropagation()
                                                        return false
                                                    }

                                                    // we have to blur this first and not close the lightbox
                                                    if (
                                                        event.key === "Escape"
                                                    ) {
                                                        event.preventDefault()
                                                        event.currentTarget.blur()
                                                        this.containerRef.current?.focus()
                                                        return false
                                                    }

                                                    if (event.key === "Tab") {
                                                        event.currentTarget.blur()
                                                        this.containerRef.current?.focus()
                                                    }
                                                    return true
                                                }}
                                                onBlur={(): void =>
                                                    this.updateDescription()
                                                }
                                                onChangedValue={(
                                                    value: string,
                                                ): void => {
                                                    this.setState({
                                                        unsyncDescription:
                                                            value,
                                                    })
                                                }}
                                            />
                                        ) : (
                                            <ReadOnly
                                                data-ft-description
                                                value={unsyncDescription}
                                                shouldAllowHTML={true}
                                            />
                                        )}
                                    </FieldItems>
                                </div>
                            </footer>
                        </div>

                        <div
                            className={b("innerContainer")}
                            onClick={hideLightbox}
                        >
                            <Action
                                autoFocus={
                                    currentIndex + 1 < attachments.length
                                }
                                ref={this.nextRef}
                                extraClasses={`${b("nav")} ${
                                    currentIndex + 1 >= attachments.length &&
                                    "is-invisible"
                                }`}
                                onClick={(
                                    event: React.MouseEvent<HTMLButtonElement>,
                                ): void => {
                                    event.stopPropagation()
                                    changeAttachmentInLightbox(1)
                                    this.setFocus()
                                }}
                                data-ft-arrow="next"
                            >
                                {Symbols.ArrowRight}
                            </Action>
                        </div>
                    </div>

                    <div className={b("bottom")} onClick={hideLightbox}>
                        <div
                            className={`${b(
                                "bottomContainer",
                            )} label is-dimmed`}
                            onClick={(
                                event: React.MouseEvent<HTMLDivElement>,
                            ): void => {
                                // to make sure we can can highlight the counters
                                event.stopPropagation()
                            }}
                        >
                            <span data-ft-current-attachment-number>
                                {currentIndex + 1}
                            </span>
                            /
                            <span data-ft-all-attachments-counter>
                                {attachments.length}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        )
    }

    setFocus = (): void => {
        const previousRef = this.previousRef
        const nextRef = this.nextRef
        const containerRef = this.containerRef
        setTimeout(() => {
            if (document.activeElement === previousRef.current) {
                if (previousRef.current?.className.includes("is-invisible")) {
                    nextRef.current?.focus()
                }
            } else if (document.activeElement === nextRef.current) {
                if (nextRef.current?.className.includes("is-invisible")) {
                    previousRef.current?.focus()
                }
            } else {
                containerRef.current?.focus()
            }
        }, 1)
    }

    onAttachmentDelete = (): void => {
        const {
            attachments,
            lightbox: { currentIndex },
            onDeleteAttachment,
            hideLightbox,
        } = this.props
        const shouldClose = attachments.length === 1

        const currentAttachment = attachments[currentIndex]
        onDeleteAttachment(currentAttachment.key)

        if (shouldClose) {
            hideLightbox()
        }
    }

    updateDescription = (): void => {
        const {
            attachments,
            lightbox: { currentIndex },
            onUpdateAttachmentDescription,
        } = this.props
        const { previousDescription, unsyncDescription } = this.state
        const currentAttachment = attachments[currentIndex]

        const newValue = unsyncDescription.trim()

        if (newValue === previousDescription) {
            return
        }

        onUpdateAttachmentDescription(currentAttachment.key, newValue)
    }
}
